import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { MapPin, MessageCircle, Video, ShoppingBag } from 'lucide-react';
import { applyWordFilter } from '../../utils/content';
import { getDisplayLocation } from '../../utils/location';
import Avatar from '../../components/common/Avatar';
import MediaCarousel from '../../components/common/MediaCarousel';
import MessageModal from '../../components/messaging/MessageModal';

const MarketplaceView = () => {
  const { posts, users, videoFiles, imageFiles, viewUserProfile, wordFilters, currentUser } = useContext(AppContext);
  const [carouselState, setCarouselState] = useState(null); // { postId, startIndex }
  const [messageSellerData, setMessageSellerData] = useState(null); // { sellerId, title, price }

  const marketplacePosts = posts.filter(p => p.type === 'marketplace');

  const openCarousel = (postId, imageIndex) => {
    setCarouselState({ postId, startIndex: imageIndex });
  };

  const closeCarousel = () => {
    setCarouselState(null);
  };

  const openMessageModal = (sellerId, title, price) => {
    setMessageSellerData({ sellerId, title, price });
  };

  const closeMessageModal = () => {
    setMessageSellerData(null);
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold text-white mb-6">Marketplace</h2>

      {marketplacePosts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
            <ShoppingBag className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-white font-semibold text-lg mb-1">Nothing for sale yet</p>
          <p className="text-gray-400 text-sm">Be the first to list your golf gear!</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {marketplacePosts.map(post => {
          const seller = users.find(u => u.id === post.userId);
          return (
            <div key={post.id} className="bg-black rounded-lg shadow border border-gray-700 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Avatar user={seller} size="md" />
                  <div>
                    <p className="font-medium text-white">{seller?.name}</p>
                    <p className="text-xs text-white">@{seller?.handle}</p>
                    <p className="text-xs text-white">{getDisplayLocation(seller?.location)}</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-slate-700 text-pure-white text-xs rounded-full">
                  {post.type === 'coaching' ? 'Coaching' : 'For Sale'}
                </span>
              </div>

              {post.marketplace?.title && <h3 className="text-lg font-bold text-white mb-1">{post.marketplace.title}</h3>}
              <p className="text-white mb-3">{applyWordFilter(post.content, wordFilters)}</p>

              {/* Display media grid (video + images combined) - 3 rows x 2 columns */}
              {(() => {
                const hasMedia = post.video || (post.imagePreviews && post.imagePreviews.length > 0);
                if (!hasMedia) return null;

                const mediaItems = [];

                // Add video first if it exists
                if (post.video) {
                  mediaItems.push({ type: 'video', filename: post.video.filename });
                }

                // Add images
                if (post.imagePreviews && post.imagePreviews.length > 0) {
                  post.imagePreviews.forEach(preview => {
                    mediaItems.push({ type: 'image', src: preview });
                  });
                }

                return (
                  <div className="mb-3 grid grid-cols-2 gap-2">
                    {mediaItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => openCarousel(post.id, index)}
                        className="w-full focus:outline-none focus:ring-2 focus:ring-[#16a34a] rounded-lg"
                      >
                        {item.type === 'video' ? (
                          <div className="w-full rounded-lg h-24 bg-gray-800 border-2 border-gray-700 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity">
                            <div className="text-center">
                              <Video className="w-8 h-8 text-white mx-auto mb-1" />
                              <p className="text-white text-xs px-2 truncate">{item.filename}</p>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={item.src}
                            alt={`Product ${index + 1}`}
                            className="w-full rounded-lg h-24 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                );
              })()}

              {post.marketplace && (
                <div className="bg-gray-900 rounded-lg p-3 mb-3">
                  <p className="text-lg font-bold text-white">${post.marketplace.price}</p>
                  <p className="text-sm text-white">Condition: {post.marketplace.condition}</p>
                  {post.marketplace.location && (
                    <p className="text-sm text-white flex items-center mt-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      {post.marketplace.location}
                    </p>
                  )}
                </div>
              )}

              {post.userId !== currentUser.id && (
                <button
                  onClick={() => openMessageModal(seller.id, post.marketplace?.title, post.marketplace?.price)}
                  className="w-full bg-[#16a34a] text-pure-white py-2 rounded-lg hover:bg-[#15803d] flex items-center justify-center space-x-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Contact Seller</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Media Carousel Modal */}
      {carouselState && (() => {
        const post = marketplacePosts.find(p => p.id === carouselState.postId);
        if (post) {
          const mediaItems = [];

          // Add video first if it exists
          if (post.video) {
            mediaItems.push({ type: 'video', filename: post.video.filename });
          }

          // Add images
          if (post.imagePreviews && post.imagePreviews.length > 0) {
            post.imagePreviews.forEach(preview => {
              mediaItems.push({ type: 'image', src: preview });
            });
          }

          if (mediaItems.length > 0) {
            return (
              <MediaCarousel
                media={mediaItems}
                initialIndex={carouselState.startIndex}
                onClose={closeCarousel}
              />
            );
          }
        }
        return null;
      })()}

      {/* Message Modal */}
      {messageSellerData && (
        <MessageModal
          recipientId={messageSellerData.sellerId}
          initialMessage={messageSellerData.title ? `Hi, I'm interested in: ${messageSellerData.title} - $${messageSellerData.price}` : ''}
          onClose={closeMessageModal}
        />
      )}
    </div>
  );
};

export default MarketplaceView;
